import type { ReactNode } from 'react';
import { cx } from '../cx';
import { ROUGH_FILL, SampleCount, sampleTier, type ThinLabels } from './thin';
import { TICK_TEXT, VALUE_TEXT, niceScale, percentOf } from './scale';
import { WIDE_ONLY, type ChartLayout } from './layout';

export type LadderStep = {
  id: string;
  /** The level ("Junior"). */
  label: ReactNode;
  /** Typical experience, in words ("0 to 2 years"). */
  detail?: ReactNode;
  p25: number;
  median: number;
  p75: number;
  sample: number;
  /** The median, formatted. */
  display: ReactNode;
};

export type CareerLadderProps = {
  /** In career order, first step first. */
  steps: LadderStep[];
  /** The step the reader is on or the headline is about: sun; the rest ink outlines. */
  highlightId?: string;
  formatTick: (value: number) => string;
  /** The thin-data words ("rough", "Too few ads"), translated. */
  thinLabels: ThinLabels;
  /** `auto` (default) switches to the narrow form in a narrow container: a shorter plot, no experience lines under the names, the rough word on its own line. */
  layout?: ChartLayout;
  /** One sentence with every step's median, for screen readers. */
  description: string;
  className?: string;
};

/**
 * Pay up a career as a staircase: each step's tread sits at its median, the shaded riser shows the
 * middle half (p25 to p75). For career paths and role pages, from Intern to Lead. Thin steps use
 * the shared look: 10 to 19 values a faint dashed step marked rough with no shading, under 10 an
 * empty step marked too few. Each step's n sits under its name.
 */
const PLOT_H: Record<ChartLayout, string> = { auto: 'h-40 @md:h-56', narrow: 'h-40', wide: 'h-56' };
const ROUGH_WORD: Record<ChartLayout, string> = { auto: '@max-md:block', narrow: 'block', wide: '' };

export function CareerLadder({
  steps,
  highlightId,
  formatTick,
  thinLabels,
  layout = 'auto',
  description,
  className,
}: CareerLadderProps) {
  const scale = niceScale(0, Math.max(...steps.map((s) => (sampleTier(s.sample) === 'full' ? s.p75 : s.median))), {
    zero: true,
  });
  const pos = (v: number) => percentOf(v, scale.min, scale.max);
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-1', className)}>
      <div className="flex gap-2" aria-hidden="true">
        <div className={cx('relative w-12 shrink-0', PLOT_H[layout])}>
          {scale.ticks.map((t) => (
            <span
              key={t}
              className={cx('absolute right-0 translate-y-1/2', TICK_TEXT)}
              style={{ bottom: `${pos(t)}%` }}
            >
              {formatTick(t)}
            </span>
          ))}
        </div>
        <div className={cx('relative flex-1', PLOT_H[layout])}>
          {scale.ticks.map((t) => (
            <span
              key={t}
              className="absolute inset-x-0 border-t border-jc-chart-grid"
              style={{ bottom: `${pos(t)}%` }}
            />
          ))}
          <div className="absolute inset-0 flex items-end">
            {steps.map((s) => {
              const hl = s.id === highlightId;
              const tier = sampleTier(s.sample);
              if (tier === 'none')
                return (
                  <div key={s.id} className="relative flex h-full flex-1 items-end justify-center pb-2">
                    <span className="px-1 text-center text-xs text-jc-ink-secondary">{thinLabels.tooFew}</span>
                  </div>
                );
              return (
                <div key={s.id} className="relative h-full flex-1">
                  <span
                    className={cx(
                      'absolute inset-x-0 bottom-0 origin-bottom motion-safe:animate-jc-grow-y',
                      hl ? 'bg-jc-chart-2' : 'bg-jc-elevated',
                      tier === 'rough' && ROUGH_FILL,
                    )}
                    style={{ height: `${pos(s.median)}%` }}
                  />
                  {tier === 'full' ? (
                    <span
                      className={cx(
                        'absolute inset-x-2 rounded-jc-info',
                        hl ? 'bg-jc-ink/15' : 'bg-jc-chart-context/60',
                      )}
                      style={{ bottom: `${pos(s.p25)}%`, height: `${pos(s.p75) - pos(s.p25)}%` }}
                    />
                  ) : null}
                  <span
                    className={cx('absolute inset-x-0 border-t-4 border-jc-ink', tier === 'rough' && 'border-dashed')}
                    style={{ bottom: `${pos(s.median)}%` }}
                  />
                  <span
                    className={cx('absolute inset-x-0 pb-1.5 text-center', VALUE_TEXT)}
                    style={{ bottom: `${pos(s.median)}%` }}
                  >
                    {tier === 'rough' ? (
                      <>
                        {s.display}
                        <span className={WIDE_ONLY[layout]}>, </span>
                        <span className={ROUGH_WORD[layout]}>{thinLabels.rough}</span>
                      </>
                    ) : (
                      s.display
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex pl-14" aria-hidden="true">
        {steps.map((s) => (
          <span key={s.id} className="flex flex-1 flex-col items-center text-center">
            <span
              className={cx('text-xs font-semibold', s.id === highlightId ? 'text-jc-ink' : 'text-jc-ink-secondary')}
            >
              {s.label}
            </span>
            {s.detail ? <span className={cx(TICK_TEXT, WIDE_ONLY[layout])}>{s.detail}</span> : null}
            <SampleCount n={s.sample} />
          </span>
        ))}
      </div>
    </div>
  );
}
